<template>
  <c-input-container
    v-bind="$attrs"
    :validation="validation"
    :display-validation="displayValidation"
    :class="['c-input', { '-has-icon': !!icon }]"
    :required="required"
  >
    <slot
      name="label-content"
      slot="label-content"
    />
    <template slot-scope="slotProps">
      <c-icon
        v-if="icon"
        :icon="icon"
        size="20"
        class="icon"
      />

      <money
        v-if="isMoney"
        v-bind="moneyAttrs"
        :class="classes"
        autocomplete="new-password"
        :placeholder="placeholder"
        v-mask="mask"
        v-model="internalValue"
        @input.once="touched = true"
      />

      <input
        v-else-if="!textarea"
        v-bind="$attrs"
        :class="classes"
        :inputmode="inputMode"
        autocomplete="new-password"
        :placeholder="placeholder"
        v-mask="mask"
        v-model="internalValue"
        @blur="touched = true; $emit('blur')"
        @focus="touched = false; $emit('focus')"
      />

      <textarea
        v-else
        :class="classes"
        :rows="rows"
        :placeholder="placeholder"
        v-bind="$attrs"
        v-model="internalValue"
        @blur="touched = true; $emit('blur')"
        @focus="touched = false; $emit('focus')"
      />
    </template>
  </c-input-container>
</template>

<script>
import { mask } from 'vue-the-mask'
import { Money } from 'v-money'

export default {
  components: { Money },
  directives: {
    mask (el, binding) {
      if (!binding.value || !binding.value.length) {
        return
      }
      return mask(el, binding)
    }
  },
  props: {
    mask: [String, Array],
    background: Boolean,
    placeholder: String,
    validation: String,
    textarea: Boolean,
    success: Boolean,
    isMoney: Boolean,
    round: Boolean,
    value: [String, Number],
    icon: String,
    moneyMask: {
      type: Object,
      default: () => ({
        prefix: 'R$ ',
        decimal: ',',
        thousands: '.'
      })
    },
    rows: {
      type: String,
      default: '4'
    },
    disabled: Boolean,
    hasError: Boolean,
    watch: {
      type: Boolean,
      default: true
    },
    required: Boolean,
    inputMode: {
      type: String,
      default: 'text'
    },
    hasHtmlProtection: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      internalValue: this.value,
      touched: false
    }
  },
  computed: {
    classes () {
      const classes = [
        'input', {
          '-has-validation': this.displayValidation || this.success,
          '-success': this.success,
          '-textarea': this.textarea,
          '-round': this.round,
          '-background': this.background,
          '-disabled': this.disabled,
          '-error': this.hasError
        }
      ]
      return classes
    },
    displayValidation () {
      return !!(!!this.validation && (this.touched || this.hasError))
    },
    moneyAttrs () {
      return {
        ...this.moneyMask,
        ...this.$attrs
      }
    }
  },
  watch: {
    value (v) {
      if (this.watch) this.internalValue = v
    },
    internalValue (v) {
      this.$emit('input', this.htmlProtection(v))
    }
  },
  methods: {
    htmlProtection (value) {
      let filteredValue = value
      if (!this.isMoney && (this.hasHtmlProtection && value.includes('<') && value.includes('</'))) {
        const start_idx = value.indexOf('<')
        const end_idx = value.lastIndexOf('>')
        const htmlToRemove = filteredValue.slice(start_idx, end_idx)
        filteredValue = filteredValue.replace(htmlToRemove, '')
      }
      return filteredValue
    }
  }
}
</script>

<style lang="scss">
@import '~@style/reference';

$c-input-disabled-background-color: #eaedef !default;
$c-input-disabled-color:            #bdc0d1 !default;

.c-input {
  & > .input {
    @extend %c-input-input;
  }

  &.-has-icon {
    position:relative;
    padding: 0;
    margin-bottom: 20px;

    & > .icon {
      position: absolute;
      top: 10px;
      left: 10px;
      fill: $icon-color;
    }

    & > .input {
      height: 20px;
      margin: 0;
      padding: 0;
      padding-left: 40px;
    }
  }
}

%c-input-input {
  outline: none;
  background-color: white;
  color: $title-color;
  border-radius: 3px;
  font-size: 14px;
  display: inline-block;
  width: 100%;
  box-shadow: none;
  border: 1px solid $border-color;
  padding: 5px 10px;
  transition: border-color .3s ease,
              box-shadow .3s ease;
  will-change: border-color, box-shadow;
  cursor: pointer;
  font-family: $base-font-family;

  &::placeholder {
    font-size: 14px;
    color: $icon-color;
    font-weight: 400;
    font-family: $base-font-family;
  }

  &:not(.-textarea) { height: 40px; }

  &:not(.-has-validation) {
    &:hover,
    &:active,
    &:focus {
      border-color: $secondary-color-placeholder;
      border-color: var(--color-secondary);
      box-shadow: 0 0 0 2px $secondary-color-placeholder;
      box-shadow: 0 0 0 2px var(--color-secondary);
    }
  }

  &.-has-validation, .-error {
    box-shadow: none;
    outline: none;
    outline-offset: 0;
    border-color: $failure-color;

    &.-success { border-color: $success-color; }
  }

  &.-round { border-radius: 20px; }

  &.-background { background-color: $background-color; }

  &.-disabled {
    background-color: $c-input-disabled-background-color;
    border-color: $c-input-disabled-background-color;

    color: $c-input-disabled-color;
    font-weight: 300;
    pointer-events: none;

    &:hover {
      cursor: not-allowed;
      pointer-events: all;
    }
  }

  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/CComponents/CInput.vue